import { useContext, useState } from 'react';
import {
  Banner,
  Buttons,
  FlexContainer,
  ModalContext,
  Sizes,
  Text,
  useResize,
} from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import StorageService from 'Services/storage';
import { useTranslation } from 'Hooks/useTranslation';
import { friendsRewards } from 'Containers/Referral/data';
import { ShareModal } from 'Components/Modal/components';
import ReferralBox from '../ReferralBox';
import ClabeModal from '../../../../components/Modal/components/ClabeModal';
import * as Styled from './styles';
import { ReferralTrackingProps } from './types';

const isClosedInLocalStorage =
  StorageService.getItem('tx_notification_referral_tracking_clabe') !== 'false';

const saveNotificationToLocalStorage = (id: string): void => {
  StorageService.setItem(id, 'false');
};

const MODAL_ID = 'referral_tracking_clabe';

const NOTIFICATION_KEY = 'Notification';
const WARNING_KEY = 'Warning';

const ReferralTracking = ({
  profits,
  referralData,
  hasReferral,
  refetch,
}: ReferralTrackingProps): JSX.Element => {
  const { showModal } = useContext(ModalContext);
  const { t } = useTranslation(locizeNS.MGM);
  const [showNotification, setShowNotification] = useState(isClosedInLocalStorage);
  const { isMobile } = useResize();

  const openClabeModal = (): void => {
    showModal({
      comp: (
        <ClabeModal
          data={{ clabe: referralData.clabe, fullName: referralData.fullName, refetch }}
        />
      ),
    });
    if (referralData?.clabe) {
      setShowNotification(false);
      saveNotificationToLocalStorage(`tx_notification_${MODAL_ID}`);
    }
  };

  const openShareModal = (): void => {
    showModal({
      comp: <ShareModal isFiling={false} />,
    });
  };

  const rewards = friendsRewards(referralData?.referredAmount as number, 10, isMobile ? 3 : 7);

  return (
    <Styled.Tracking justify="center">
      <Styled.TrackingContent direction="column" justify="start" gap="dimension_24">
        <Styled.TrackingHeader align="center" justify="space-between">
          <Text type="headline" size="XS_L_M" color="dark">
            {t('Referral.Tracking.Title')}
          </Text>
          <Buttons.Classic
            label={t('Referral.Tracking.EditClabe')}
            size="s"
            mode="success_light"
            onClick={openClabeModal}
          />
        </Styled.TrackingHeader>
        {showNotification && profits?.paid?.total > 0 && (
          <Banner
            id="referral_tracking_iban"
            label={t(
              `Referral.Tracking.${referralData?.clabe ? NOTIFICATION_KEY : WARNING_KEY}Title`
            )}
            description={t(
              `Referral.Tracking.${referralData?.clabe ? NOTIFICATION_KEY : WARNING_KEY}Description`,
              {
                clabe: (referralData?.clabe as string)?.slice(-4),
              }
            )}
            hasIcon
            buttonLabel={t(
              `Referral.Tracking.${referralData?.clabe ? NOTIFICATION_KEY : WARNING_KEY}CTA`
            )}
            onClick={openClabeModal}
            mode={referralData?.clabe ? 'default' : 'warning'}
            position="vertically"
            hasClose={!referralData?.clabe}
            layout="embed"
            size={Sizes.M}
            titleSize={Sizes.S}
          />
        )}
        {!hasReferral ? (
          <Styled.TrackingBox direction="column" align="start" gap="dimension_24">
            <Text type="paragraphs" size="M_M_S" color="dark">
              {t('Referral.Tracking.Box.No.Title')}
            </Text>
            <Styled.TrackingContainer gap="dimension_16" justify="space-between">
              <Styled.TrackingProgress justify="space-between">
                {rewards.map(reward => (
                  <FlexContainer
                    key={reward.title}
                    direction="column"
                    align="center"
                    gap="dimension_08"
                  >
                    <Text color="dark" type="paragraphs" size="XS_M_S">
                      {reward.title}
                    </Text>
                    <Text color="dark" type="paragraphs" size="L_XL_S">
                      {reward.amount}
                    </Text>
                  </FlexContainer>
                ))}
              </Styled.TrackingProgress>
              <Styled.ExtraFriend>
                <FlexContainer
                  direction="column"
                  align="center"
                  justify="center"
                  gap="dimension_08"
                >
                  <Text color="dark" type="paragraphs" size="S_M_S" align="center">
                    {t('Referral.Tracking.Box.AditonalFriend')}
                  </Text>
                  <Text color="dark" type="paragraphs" size="L_XL_S">
                    {t('Referral.Tracking.Box.MXN')}
                  </Text>
                </FlexContainer>
              </Styled.ExtraFriend>
            </Styled.TrackingContainer>
            <Styled.InviteLink type="button" onClick={openShareModal}>
              <Text type="paragraphs" size="S_XL_S" color="fresh">
                {t('Referral.Tracking.Box.Share')}
              </Text>
            </Styled.InviteLink>
          </Styled.TrackingBox>
        ) : (
          <ReferralBox profits={profits} />
        )}
      </Styled.TrackingContent>
    </Styled.Tracking>
  );
};

export default ReferralTracking;
